import { useState } from 'react'
import { Link } from 'react-router'
import { api, ErroreApi } from '../servizi/api.ts'
import { Avviso } from '../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Etichetta, Modale, type TonoEtichetta } from '../componenti/base.tsx'
import { Guscio, conteggio, messaggioErrore, usaElenco } from '../componenti/elenco.tsx'

/**
 * «Le mie esportazioni» (465; segnalazione del 19/10/2026: «Esporta in XLSX» metteva in coda il file
 * e mandava la notifica, ma non c'era dove scaricarlo). Le esportazioni chieste da chi guarda, con lo
 * stato; «Scarica» chiede all'API un collegamento di cinque minuti, e lo scaricamento resta nel
 * registro eventi. Il file si conserva sette giorni: dopo va rigenerato dalla pagina Report.
 */

type Esportazione = {
  id: string; tipo: 'aggregato' | 'nominativo' | 'ministeriale'; modello: string | null; formato: string
  righe: number | null; richiesto_il: string; completato_il: string | null; scade_il: string | null
  stato_vista: 'in_coda' | 'pronta' | 'scaduta' | 'errore' | 'annullata'
  serve_approvazione: boolean; soglia: number
  /** Stato dell'ultima richiesta di approvazione, se c'e'. */
  approvazione: 'in_attesa' | 'approvata' | 'respinta' | 'scaduta' | 'annullata' | null
}

const TIPI: Record<Esportazione['tipo'], string> = {
  aggregato: 'Riepilogo dei report', nominativo: 'Elenco nominativo', ministeriale: 'Modello ministeriale',
}
const STATI: Record<Esportazione['stato_vista'], { etichetta: string; tono: TonoEtichetta }> = {
  in_coda: { etichetta: 'in preparazione', tono: 'informativo' },
  pronta: { etichetta: 'pronta', tono: 'positivo' },
  scaduta: { etichetta: 'scaduta', tono: 'neutro' },
  errore: { etichetta: 'non riuscita', tono: 'critico' },
  annullata: { etichetta: 'annullata', tono: 'neutro' },
}

const quando = (v: string) => new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })

export function Esportazioni() {
  const { dati, errore, ricarica, finestra } = usaElenco<Esportazione>('/report/esportazioni')
  const [erroreAzione, setErroreAzione] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState<string | null>(null)
  const [approvazione, setApprovazione] = useState<Esportazione | null>(null)

  async function scarica(e: Esportazione) {
    setErroreAzione(null); setEsito(null); setInCorso(e.id)
    try {
      const { url } = await api.get<{ url: string }>(`/report/esportazioni/${e.id}/scarica`)
      window.open(url, '_blank', 'noopener')
    } catch (err) {
      setErroreAzione(messaggioErrore(err))
      // Lo stato puo' essere cambiato (scaduta, approvazione): l'elenco si rilegge.
      if (err instanceof ErroreApi && err.stato === 409) ricarica()
    } finally {
      setInCorso(null)
    }
  }

  /** Che cosa dire, e che cosa proporre, per ogni stato. */
  function comandi(e: Esportazione) {
    if (e.stato_vista === 'in_coda') return <span className="piccolo tenue">Arriva una notifica quando è pronta.</span>
    if (e.stato_vista === 'scaduta') {
      return <span className="piccolo tenue">Il file si conserva sette giorni: rigeneralo da <Link to="/report">Report</Link>.</span>
    }
    if (e.stato_vista === 'errore') {
      return <span className="piccolo tenue">Non si è potuto preparare: rigeneralo da <Link to="/report">Report</Link>.</span>
    }
    if (e.stato_vista !== 'pronta') return null
    if (e.serve_approvazione && e.approvazione !== 'approvata') {
      return e.approvazione === 'in_attesa'
        ? <span className="piccolo tenue">Approvazione chiesta: si scarica quando è approvata.</span>
        : (
          <Bottone variante="secondario" dimensione="piccolo" onClick={() => { setEsito(null); setApprovazione(e) }}>
            Chiedi l’approvazione
          </Bottone>
        )
    }
    return (
      <Bottone variante="primario" dimensione="piccolo" inCorso={inCorso === e.id} onClick={() => void scarica(e)}>
        Scarica
      </Bottone>
    )
  }

  return (
    <>
      {esito && <Avviso tipo="successo">{esito}</Avviso>}
      {erroreAzione && <Avviso tipo="errore">{erroreAzione}</Avviso>}
      <Guscio titolo="Le mie esportazioni" errore={errore} dati={dati} finestra={finestra}
              sottotitolo={conteggio(finestra.totale, 'esportazione', 'esportazioni')}
              vuoto="Nessuna esportazione. Si chiedono dalla pagina Report, con «Esporta in XLSX»."
              azioni={<>
                <Link className="bottone bottone-discreto" to="/report">Torna ai report</Link>
                <Bottone variante="secondario" onClick={ricarica}>Aggiorna</Bottone>
              </>}
              intestazioni={['Chiesta il', 'Che cosa', 'Righe', 'Stato', 'Disponibile fino al',
                             <span className="solo-lettore">Comandi</span>]}>
        {dati?.map((e) => (
          <tr key={e.id}>
            <td data-etichetta="Chiesta il">{quando(e.richiesto_il)}</td>
            <td data-etichetta="Che cosa">{`${TIPI[e.tipo] ?? e.tipo} · ${e.formato.toUpperCase()}`}</td>
            <td data-etichetta="Righe">{e.righe ?? '—'}</td>
            <td data-etichetta="Stato">
              <Etichetta tono={STATI[e.stato_vista].tono}>{STATI[e.stato_vista].etichetta}</Etichetta>
              {e.serve_approvazione && e.stato_vista === 'pronta' && e.approvazione === 'respinta' && (
                <div className="piccolo tenue">Approvazione respinta</div>
              )}
            </td>
            <td data-etichetta="Disponibile fino al">
              {e.stato_vista === 'pronta' && e.scade_il ? quando(e.scade_il) : '—'}
            </td>
            <td data-etichetta="Comandi">{comandi(e)}</td>
          </tr>
        ))}
      </Guscio>

      {approvazione && (
        <ChiediApprovazione esportazione={approvazione} chiudi={() => setApprovazione(null)}
                            fatto={() => {
                              setApprovazione(null)
                              setEsito('Richiesta inviata: chi può approvarla riceve un avviso. Quando è approvata, qui compare «Scarica».')
                              ricarica()
                            }} />
      )}
    </>
  )
}

/**
 * Un elenco nominativo oltre la soglia si scarica con il via libera di un'altra persona (Q-J06,
 * approvazione `export_identificativo`): la richiesta porta il motivo.
 */
function ChiediApprovazione({ esportazione, chiudi, fatto }: {
  esportazione: Esportazione; chiudi: () => void; fatto: () => void
}) {
  const [motivo, setMotivo] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function chiedi() {
    setErrore(null)
    if (motivo.trim().length < 10) { setErrore('Scrivi perché serve l’elenco: almeno qualche parola.'); return }
    setInCorso(true)
    try {
      await api.post('/approvazioni', {
        tipo: 'export_identificativo', oggetto_tipo: 'export', oggetto_id: esportazione.id, motivazione: motivo.trim(),
      })
      fatto()
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo="Chiedi l’approvazione per scaricare" chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} onClick={() => void chiedi()}>Chiedi</Bottone>
            </>}>
      <p>
        L’elenco ha {esportazione.righe} righe con nomi e cognomi, più delle {esportazione.soglia} che si scaricano
        senza un secondo parere. Lo approva un’altra persona fra Direzione, DPO, Referente privacy e Project Manager.
      </p>
      <AreaTesto etichetta="Perché serve" obbligatorio righe={3} maxLength={1000} valore={motivo} cambia={setMotivo}
                 errore={errore} />
    </Modale>
  )
}
